/**
 * LanguageSelector Component
 *
 * Dropdown component for selecting the application language.
 * Displays current language with flag/code and available options.
 *
 * Features:
 * - Keyboard navigable (Tab, Enter, Escape, Arrow keys)
 * - ARIA-compliant with proper labels and roles
 * - Immediate language switching on selection
 * - Mobile-friendly touch targets (44x44px minimum)
 *
 * @validates Requirements 7.1, 7.2, 7.3, 7.4, 7.5
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { SUPPORTED_LANGUAGES, VISIBLE_LANGUAGES, type LocaleCode } from '../../services/languageService';

/**
 * LanguageSelector Props
 */
export interface LanguageSelectorProps {
  /** Additional CSS classes */
  className?: string;
  /** Variant for different display contexts */
  variant?: 'header' | 'mobile';
}

/**
 * LanguageSelector Component
 *
 * Displays the current language with a dropdown to switch between available languages.
 * Supports keyboard navigation and is accessible via screen readers.
 */
export function LanguageSelector({
  className = '',
  variant = 'header',
}: LanguageSelectorProps): JSX.Element {
  const { t, currentLanguage, setLanguage } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);

  // Get current language config with fallback to first language
  // Default fallback ensures TypeScript knows this is always defined
  const defaultLangConfig = { code: 'en' as LocaleCode, name: 'English', nativeName: 'English', flag: '🇬🇧' };
  const currentLangConfig = SUPPORTED_LANGUAGES.find(
    (lang) => lang.code === currentLanguage
  ) ?? defaultLangConfig;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          setIsOpen(false);
          setFocusedIndex(-1);
          buttonRef.current?.focus();
          event.preventDefault();
          break;

        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
            setFocusedIndex(0);
          } else {
            setFocusedIndex((prev) =>
              prev < VISIBLE_LANGUAGES.length - 1 ? prev + 1 : 0
            );
          }
          break;

        case 'ArrowUp':
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
            setFocusedIndex(VISIBLE_LANGUAGES.length - 1);
          } else {
            setFocusedIndex((prev) =>
              prev > 0 ? prev - 1 : VISIBLE_LANGUAGES.length - 1
            );
          }
          break;

        case 'Enter':
        case ' ':
          event.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
            setFocusedIndex(0);
          } else if (focusedIndex >= 0 && focusedIndex < VISIBLE_LANGUAGES.length) {
            const selectedLang = VISIBLE_LANGUAGES[focusedIndex];
            if (selectedLang) {
              handleLanguageSelect(selectedLang.code);
            }
          }
          break;

        case 'Tab':
          if (isOpen) {
            setIsOpen(false);
            setFocusedIndex(-1);
          }
          break;

        case 'Home':
          if (isOpen) {
            event.preventDefault();
            setFocusedIndex(0);
          }
          break;

        case 'End':
          if (isOpen) {
            event.preventDefault();
            setFocusedIndex(VISIBLE_LANGUAGES.length - 1);
          }
          break;
      }
    },
    [isOpen, focusedIndex]
  );

  // Handle language selection
  const handleLanguageSelect = useCallback(
    async (locale: LocaleCode) => {
      await setLanguage(locale);
      setIsOpen(false);
      setFocusedIndex(-1);
      buttonRef.current?.focus();
    },
    [setLanguage]
  );

  // Toggle dropdown
  const toggleDropdown = useCallback(() => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      // Find current language index for initial focus
      const currentIndex = VISIBLE_LANGUAGES.findIndex(
        (lang) => lang.code === currentLanguage
      );
      setFocusedIndex(currentIndex >= 0 ? currentIndex : 0);
    } else {
      setFocusedIndex(-1);
    }
  }, [isOpen, currentLanguage]);

  // Focus the focused item when dropdown opens
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && listboxRef.current) {
      const items = listboxRef.current.querySelectorAll('[role="option"]');
      (items[focusedIndex] as HTMLElement)?.focus();
    }
  }, [isOpen, focusedIndex]);

  // Styles based on variant
  const isMobile = variant === 'mobile';

  const buttonClasses = isMobile
    ? `flex items-center gap-2 w-full min-h-[44px] px-4 py-3 rounded-lg font-medium text-slate-600 hover:bg-slate-100 transition-colors ${className}`
    : `flex items-center gap-2 min-h-[44px] min-w-[44px] px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors ${className}`;

  const dropdownClasses = isMobile
    ? 'absolute left-0 right-0 bottom-full mb-1 py-1 bg-white rounded-lg shadow-lg border border-slate-200 z-50 max-h-[300px] overflow-y-auto'
    : 'absolute right-0 mt-1 py-1 bg-white rounded-lg shadow-lg border border-slate-200 z-50 min-w-[160px]';

  return (
    <div
      ref={containerRef}
      className={`relative ${isMobile ? 'w-full' : ''}`}
      onKeyDown={handleKeyDown}
    >
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleDropdown}
        className={buttonClasses}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t('a11y.languageSelector')}
        aria-controls="language-listbox"
      >
        <span className="text-lg" aria-hidden="true">
          {currentLangConfig.flag}
        </span>
        <span className={isMobile ? 'flex-1 text-left' : 'sr-only md:not-sr-only'}>
          {currentLangConfig.nativeName}
        </span>
        {/* Chevron icon */}
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <ul
          ref={listboxRef}
          id="language-listbox"
          role="listbox"
          aria-label={t('language.selector.label')}
          aria-activedescendant={
            focusedIndex >= 0 && focusedIndex < VISIBLE_LANGUAGES.length
              ? `language-option-${VISIBLE_LANGUAGES[focusedIndex]?.code ?? ''}`
              : undefined
          }
          className={dropdownClasses}
        >
          {VISIBLE_LANGUAGES.map((lang, index) => {
            const isSelected = lang.code === currentLanguage;
            const isFocused = index === focusedIndex;

            return (
              <li
                key={lang.code}
                id={`language-option-${lang.code}`}
                role="option"
                aria-selected={isSelected}
                tabIndex={isFocused ? 0 : -1}
                onClick={() => handleLanguageSelect(lang.code)}
                onMouseEnter={() => setFocusedIndex(index)}
                className={`flex items-center gap-3 px-4 py-3 min-h-[44px] cursor-pointer transition-colors
                  ${isFocused ? 'bg-primary-50' : ''}
                  ${isSelected ? 'text-primary-700 font-medium' : 'text-slate-700'}
                  hover:bg-primary-50`}
              >
                <span className="text-lg" aria-hidden="true">
                  {lang.flag}
                </span>
                <span className="flex-1">{lang.nativeName}</span>
                {isSelected && (
                  <svg
                    className="w-5 h-5 text-primary-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
